import React, { useState, useEffect, useRef } from "react";
import Header from "./Header";
import Footer from "./Footer";
import SEO from "./SEO";
import { Link, useSearchParams } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import {
  getPublicDirectoryListings,
  getMyDirectoryListing,
} from "../services/directoryService";
import DirectoryApplyForm from "./DirectoryApplyForm";
import ListingCard from "./ListingCard";
// Single source of truth — the landing page owns the canonical list, photos included.
import { TESTIMONIALS } from "./newLanding/NewLandingTestimonials";
import { Search, Globe, Mail, BookOpen, Loader, ChevronUp, ChevronDown, Star, Check } from "lucide-react";
import "./Directory.css";

const PRACTICE_AREAS = [
  "All",
  "Civil Litigation",
  "Business / Corporate / Contracts",
  "Labor & Employment",
  "Criminal Defense / Post-Conviction",
  "Personal Injury / Insurance",
  "Family Law",
  "Estate Planning / Probate",
  "Immigration",
  "Real Estate / Construction",
  "Bankruptcy / Debt Relief",
];

const PA_TAG_CLASS = {
  "Civil Litigation": "pa-litigation",
  "Business / Corporate / Contracts": "pa-business",
  "Labor & Employment": "pa-employment",
  "Criminal Defense / Post-Conviction": "pa-criminal",
  "Personal Injury / Insurance": "pa-injury",
  "Family Law": "pa-family",
  "Estate Planning / Probate": "pa-estate",
  "Immigration": "pa-immigration",
  "Real Estate / Construction": "pa-realestate",
  "Bankruptcy / Debt Relief": "pa-bankruptcy",
};


/**
 * Static illustrative previews shown only while the real directory is empty.
 * NOT wired to search or filtering — once real listings exist these disappear
 * on their own. Delete this block when they are no longer needed.
 */
const SAMPLE_LISTINGS = [
  {
    id: "sample-attorney",
    name: "Alex Morgan, Esq.",
    role: "Attorney",
    location: "San Diego, California",
    practiceAreas: ["Civil Litigation", "Immigration", "Estate Planning / Probate"],
    verified: true,
  },
  {
    id: "sample-paralegal",
    name: "Jordan Lee",
    role: "Paralegal",
    location: "Austin, Texas",
    practiceAreas: [
      "Personal Injury / Insurance",
      "Family Law",
      "Real Estate / Construction",
    ],
    verified: false,
  },
];

const VERIFIED_HINT =
  "Confirmed by LawFusion — not a check of bar license or credentials.";

export default function Directory() {
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState("directory");
  // Closed on landing so the page leads with "Join the Free Directory", unless
  // we were sent here to set a listing up — a directory login by someone who
  // does not have one yet lands on /directory?join=1.
  const [searchParams] = useSearchParams();
  const [joinOpen, setJoinOpen] = useState(searchParams.get("join") === "1");

  // Someone who already has a listing does not need to be pitched on joining.
  //
  // Three states, not two: null means the lookup has not answered yet, and the
  // join block stays hidden until it has. Keyed to the auth context, not to a
  // token in localStorage: sign-in is cookie-first, and the token is only
  // stored for the Safari fallback, so a signed-in Chrome user had no token,
  // was treated as signed-out, and was pitched on joining a directory they
  // are already in (QA #8).
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [hasListing, setHasListing] = useState(null);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!isAuthenticated) {
      setHasListing(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        // The request carries the session cookie; the Bearer header is the
        // Safari fallback when a token exists.
        const token = localStorage.getItem("token");
        const mine = await getMyDirectoryListing(
          token ? { Authorization: `Bearer ${token}` } : {},
        );
        if (!cancelled) setHasListing(!!mine);
      } catch (e) {
        // Treated as "no listing": the worst case is showing the join block to
        // someone who has one, which is where this started.
        if (!cancelled) setHasListing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authLoading]);

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // "View Public Listing" links an attorney straight at their own entry.
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  // ...and names the listing, so we can put it in front of them instead of
  // leaving them to scan the page for it.
  const highlightId = searchParams.get("highlight") || "";
  const [highlighted, setHighlighted] = useState("");
  const highlightHandled = useRef(false);
  const resultsRef = useRef(null);
  // Nothing preselected on load.
  const [selectedArea, setSelectedArea] = useState("");
  const [roleFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(24);
  const [limitInput, setLimitInput] = useState("24");

  useEffect(() => {
    setLimitInput(String(limit));
  }, [limit]);

  const handleSearchChange = (val) => {
    setSearchTerm(val);
    setPage(1);
  };

  const handleAreaChange = (area) => {
    setSelectedArea(area);
    setPage(1);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchListings = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = { page, limit };
        if (searchTerm.trim()) params.search = searchTerm.trim();
        if (selectedArea && selectedArea !== "All") params.practiceArea = selectedArea;
        if (roleFilter !== "All") params.role = roleFilter;

        const res = await getPublicDirectoryListings(params);
        if (isMounted) {
          setListings(res.listings || []);
          if (res.pagination) {
            setTotalPages(res.pagination.totalPages || 1);
            setTotal(res.pagination.total || 0);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Failed to load directory listings.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const debounce = setTimeout(fetchListings, 300);
    return () => {
      isMounted = false;
      clearTimeout(debounce);
    };
  }, [searchTerm, selectedArea, roleFilter, page, limit]);

  /**
   * Bring the attorney's own listing into view once it has rendered.
   *
   * Only runs for a `highlight` id, so an ordinary visitor typing in the
   * search box is never scrolled around, and only once per arrival. If the id
   * is not on this page the results heading is the fallback — still better
   * than landing on the hero and hunting.
   */
  useEffect(() => {
    if (!highlightId || loading || highlightHandled.current) return;

    const card = document.getElementById(`listing-${highlightId}`);
    const target = card || resultsRef.current;
    if (!target) return;

    highlightHandled.current = true;
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    target.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: card ? "center" : "start",
    });

    if (!card) return;
    setHighlighted(highlightId);
    const timer = setTimeout(() => setHighlighted(""), 2800);
    return () => clearTimeout(timer);
  }, [highlightId, loading, listings]);

  // An active search/filter that matches nothing is a plain "no results" case;
  // an empty directory is what the sample previews are for.
  const hasActiveQuery =
    !!searchTerm.trim() || (!!selectedArea && selectedArea !== "All");

  const startRecord = total === 0 ? 0 : (page - 1) * limit + 1;
  const endRecord = Math.min(page * limit, total);

  const joinPanelRef = useRef(null);
  const tabContentRef = useRef(null);
  const isFirstTabRender = useRef(true);
  const isFirstJoinRender = useRef(true);

  const openJoinPanel = () => setJoinOpen(true);
  const closeJoinPanel = () => setJoinOpen(false);

  useEffect(() => {
    const first = isFirstJoinRender.current;

    if (!joinOpen) {
      isFirstJoinRender.current = false;
      return;
    }

    // Arriving on /directory?join=1 is a request for the form — a directory
    // login by someone without a listing lands here. The first render used to
    // be skipped outright, so they were dropped at the top of the page and
    // left to find the form among everyone else's listings.
    //
    // On that first render the listings are still loading and will reflow the
    // page underneath, so the scroll waits for them to settle. A visitor who
    // opened the panel themselves is already looking at it and gets the
    // smooth scroll immediately.
    if (first && loading) return;
    isFirstJoinRender.current = false;

    const panel = joinPanelRef.current;
    if (!panel) return;
    requestAnimationFrame(() => {
      panel.scrollIntoView({
        behavior: first ? "auto" : "smooth",
        block: "start",
      });
    });
  }, [joinOpen, loading]);

  useEffect(() => {
    if (isFirstTabRender.current) {
      isFirstTabRender.current = false;
      return;
    }
    if (tabContentRef.current) {
      requestAnimationFrame(() => {
        tabContentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [activeTab]);

  return (
    <div className={`directory-container ${darkMode ? "dark" : ""}`}>
      <SEO
        title="Legal Professional Directory | LawFusion"
        path="/directory"
        description="Find top legal professionals, attorneys, and paralegals in LawFusion's free public directory."
      />
      <Header />

      <main className="directory-content">
        <div className="directory-hero">
          <h1 className="directory-title">LawFusion Directory</h1>
          <p className="directory-subtitle">
            Find attorneys and paralegals by practice area — or join free to increase your
            visibility and connect with referral sources and potential clients.
          </p>

          <div className="local-switch" role="tablist" aria-label="Directory sections">
            <button
              type="button"
              className={`switch-tab ${activeTab === "directory" ? "active" : ""}`}
              role="tab"
              aria-selected={activeTab === "directory"}
              onClick={() => setActiveTab("directory")}
            >
              Directory
            </button>
            <button
              type="button"
              className={`switch-tab ${activeTab === "reviews" ? "active" : ""}`}
              role="tab"
              aria-selected={activeTab === "reviews"}
              onClick={() => setActiveTab("reviews")}
            >
              Attorney Reviews
            </button>
          </div>
        </div>

        {/* Join CTA + shared sign-up panel. Hidden once we know the visitor
            already has a listing — they arrived here from "My Directory
            Listing" and were being asked to join something they are in. */}
        <section
          className="join-section"
          hidden={hasListing !== false && !joinOpen}
        >
          <div className="join-cta">
            <div className="join-cta-text">
              <h2>Are you an attorney or paralegal? Join the Free LawFusion Directory</h2>
              <p>
                Get discovered by people{" "}
                <Link to="/ask" className="join-cta-link">
                  asking legal questions on LawFusion
                </Link>
                , plus attorneys and referral sources searching the directory.
              </p>
            </div>
            <button type="button" className="btn-join" onClick={() => (joinOpen ? closeJoinPanel() : openJoinPanel())}>
              {joinOpen ? "Close" : "Join the Directory"}
            </button>
          </div>

          <div ref={joinPanelRef} className={`join-panel ${joinOpen ? "open" : ""}`}>
            <div className="join-panel-inner">
              <DirectoryApplyForm open={joinOpen} onClose={closeJoinPanel} />
            </div>
          </div>
        </section>

        <div ref={tabContentRef}>
        {activeTab === "directory" ? (
          <section className="listings-section">
            <div className="search-bar">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search by name, firm, city, state, role, or practice area..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>

            <div className="filters">
              {PRACTICE_AREAS.map((area) => (
                <button
                  key={area}
                  type="button"
                  className={`pill ${selectedArea === area ? "active" : ""}`}
                  onClick={() => handleAreaChange(area)}
                >
                  {area}
                </button>
              ))}
            </div>

            <div className="section-heading" ref={resultsRef}>
              <div>
                <h2>Directory Listings</h2>
                <p className="listing-intro">
                  Attorneys and paralegals who use LawFusion and/or have chosen to be listed in
                  our Free Directory.
                </p>
              </div>
              <span className="result-count">
                {loading ? "Loading…" : `${total} ${total === 1 ? "listing" : "listings"}`}
              </span>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "4rem 1rem" }}>
                <Loader className="spin" size={32} style={{ marginBottom: "1rem" }} />
                <p style={{ opacity: 0.7 }}>Loading legal directory...</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: "center", padding: "4rem 1rem", color: "#ef4444" }}>
                <p>{error}</p>
              </div>
            ) : listings.length === 0 && hasActiveQuery ? (
              <div className="empty-state">
                <BookOpen size={52} style={{ opacity: 0.4, marginBottom: "1rem" }} />
                <h3>No Results</h3>
                <p>No listings match your search. Try a different term or practice area.</p>
              </div>
            ) : listings.length === 0 ? (
              <>
                <div className="sample-grid">
                  {SAMPLE_LISTINGS.map((sample) => (
                    <article className="sample-card" key={sample.id}>
                      <div className="sample-top">
                        <span className="sample-badge">SAMPLE LISTING</span>
                        <span className="l-role-badge">{sample.role}</span>
                      </div>
                      <h3 className="l-name">{sample.name}</h3>
                      <div className="l-meta">{sample.location}</div>
                      <div className="l-tags">
                        {sample.practiceAreas.map((pa) => (
                          <span key={pa} className={`l-tag ${PA_TAG_CLASS[pa] || "pa-other"}`}>
                            {pa}
                          </span>
                        ))}
                      </div>
                      <div className="l-links">
                        {/* Inert on purpose — these are illustrations, not contacts. */}
                        <span className="l-link is-sample">
                          <Mail size={15} /> Contact Email
                        </span>
                        <span className="l-link is-sample">
                          <Globe size={15} /> Website
                        </span>
                      </div>
                      {sample.verified && (
                        <div className="l-verified l-verified-sample" title={VERIFIED_HINT}>
                          <Check size={13} strokeWidth={3} /> Listing Verified
                        </div>
                      )}
                      <p className="sample-note">
                        Example only — this is not a real directory member.
                      </p>
                    </article>
                  ))}
                </div>

                <div style={{ textAlign: "center" }}>
                  <button type="button" className="btn-join" onClick={openJoinPanel}>
                    Join the Free Directory
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="listing-grid">
                  {listings.map((item) => (
                    <ListingCard
                      listing={item}
                      key={item._id}
                      id={`listing-${item._id}`}
                      className={
                        highlighted === item._id ? "listing-card-highlight" : ""
                      }
                    />
                  ))}
                </div>

                <div className="directory-pagination">
                  <div className="directory-pagination-info">
                    Showing {startRecord} to {endRecord} of {total} listings
                  </div>

                  <div className="directory-pagination-limit">
                    <span>Rows per page:</span>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.125rem" }}>
                      <button
                        type="button"
                        className="directory-page-btn"
                        onClick={() => {
                          const newLimit = Math.min(100, limit + 5);
                          setLimit(newLimit);
                          setPage(1);
                        }}
                        disabled={limit >= 100}
                        title="Increase page size"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <input
                        type="number"
                        min="5"
                        max="100"
                        value={limitInput}
                        onChange={(e) => setLimitInput(e.target.value)}
                        onBlur={(e) => {
                          const numValue = parseInt(e.target.value, 10);
                          if (isNaN(numValue) || numValue < 5) {
                            setLimit(5);
                            setLimitInput("5");
                          } else if (numValue > 100) {
                            setLimit(100);
                            setLimitInput("100");
                          } else {
                            setLimit(numValue);
                          }
                          setPage(1);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                        }}
                        className="directory-limit-input"
                        title="Enter custom page size (5-100)"
                      />
                      <button
                        type="button"
                        className="directory-page-btn"
                        onClick={() => {
                          const newLimit = Math.max(5, limit - 5);
                          setLimit(newLimit);
                          setPage(1);
                        }}
                        disabled={limit <= 5}
                        title="Decrease page size"
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="directory-pagination-controls">
                    <button type="button" className="directory-page-btn" onClick={() => setPage(1)} disabled={page <= 1}>
                      {"<<"}
                    </button>
                    <button
                      type="button"
                      className="directory-page-btn"
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page <= 1}
                    >
                      {"<"}
                    </button>
                    <span style={{ padding: "0 0.5rem", fontSize: "0.875rem" }}>
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="directory-page-btn"
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page >= totalPages}
                    >
                      {">"}
                    </button>
                    <button
                      type="button"
                      className="directory-page-btn"
                      onClick={() => setPage(totalPages)}
                      disabled={page >= totalPages}
                    >
                      {">>"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        ) : (
          <section className="testimonials-section">
            <p className="t-eyebrow">ATTORNEY TESTIMONIALS</p>
            <h2 className="t-heading">What Attorneys Are Saying About LawFusion</h2>
            <div className="t-grid">
              {TESTIMONIALS.map((t) => (
                <article className="t-card" key={t.name}>
                  <div className="t-tags">
                    {(t.practiceAreas || []).map((pa) => (
                      <span
                        key={pa.text}
                        className={`t-tag ${PA_TAG_CLASS[pa.text] || `pa-${pa.class || "other"}`}`}
                      >
                        {pa.text}
                      </span>
                    ))}
                  </div>
                  <div className="t-stars" aria-label="5 out of 5 stars">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={16} fill="currentColor" stroke="none" />
                    ))}
                  </div>
                  <p className="t-quote">"{t.quote}"</p>
                  <div className="t-person">
                    {t.image ? (
                      <img className="t-avatar t-avatar-img" src={t.image} alt="" loading="lazy" />
                    ) : (
                      <div className="t-avatar">{t.initials}</div>
                    )}
                    <div>
                      <div className="t-name">{t.name}</div>
                      <div className="t-title">{t.title}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <p className="t-disclosure">
              Some testimonials are from compensated testers who received complimentary access.
            </p>
          </section>
        )}
        </div>

        {/* FINAL CTA. Someone who already has a listing — typically here from
            "View Public Listing" — is not asked to join what they are in; they
            get their own listing page instead. Held back while unknown, same
            as the join block above. */}
        {hasListing === true ? (
          <section className="final-cta">
            <h2>You're in the LawFusion Directory</h2>
            <p>
              Keep your listing current so referral sources and potential clients find the
              right details — or try LawFusion itself and see how multiple AI perspectives
              strengthen legal analysis.
            </p>
            <div className="final-cta-buttons">
              <Link to="/my-listing" className="btn-join">
                My Directory Listing
              </Link>
              <Link to="/pricing" className="btn-outline">
                Try LawFusion Free
              </Link>
            </div>
          </section>
        ) : hasListing === false ? (
          <section className="final-cta">
            <h2>Join the Free LawFusion Directory</h2>
            <p>
              Attorneys and paralegals can add a free listing to increase visibility with referral
              sources and potential clients — or try LawFusion itself and see how multiple AI
              perspectives strengthen legal analysis.
            </p>
            <div className="final-cta-buttons">
              <button type="button" className="btn-join" onClick={openJoinPanel}>
                Join the Directory
              </button>
              <Link to="/pricing" className="btn-outline">
                Try LawFusion Free
              </Link>
            </div>
          </section>
        ) : null}
      </main>

      <Footer />
    </div>
  );
}
